import { Select, Text } from "@chakra-ui/react"
import type { BRREGUnit } from "@esgt/types"
import { type RatingProfileSearchResult, useRatingProfilesQuery } from "lib/generated/graphql"
import { useMemo } from "react"
import type { FieldValues, UseFormRegister } from "react-hook-form"

/**
 * Hook that fetches rating profiles and matches a default based on the
 * selected organization's industry code.
 *
 * Consumers are responsible for integrating the returned data into their
 * own useForm / watch subscriptions — this hook has no side effects.
 */
export function useRatingProfiles(selectedOrganization: BRREGUnit, methodVersion: string) {
  const [ratingProfiles] = useRatingProfilesQuery({
    variables: { methodVersion },
  })
  const profiles = ratingProfiles.data?.ratingProfiles
  const defaultProfile = useRatingProfileMatch(selectedOrganization, profiles)

  return {
    profiles,
    defaultProfile,
    isFetching: ratingProfiles.fetching,
    hasOnlyOneProfile: profiles?.length === 1,
  }
}

interface RatingProfileSelectorProps {
  register: UseFormRegister<FieldValues>
  profiles?: RatingProfileSearchResult[]
  defaultProfile?: number
  isFetching: boolean
  currentValue?: number
}

/**
 * Presentational dropdown for picking a rating profile.
 *
 * Accepts `register` from the parent form, following the same pattern as
 * RatingPeriodSelector.
 */
export const RatingProfileSelector = ({
  register,
  profiles,
  defaultProfile,
  isFetching,
  currentValue,
}: RatingProfileSelectorProps) => {
  if (!isFetching && profiles?.length === 0) {
    return <Text color="red.500">Ingen målingsprofiler tilgjengelig for den valgte metoden.</Text>
  }

  return (
    <Select
      width="50%"
      value={currentValue || defaultProfile}
      placeholder={isFetching ? "Henter målingsprofiler..." : "Velg målingsprofil"}
      background="white"
      {...register("ratingProfileId", { required: true, disabled: isFetching })}
    >
      {profiles?.map((rating, index) => (
        <option key={index} value={rating.id}>
          {rating.name} {rating.methodVersion} — {rating.description}
        </option>
      ))}
    </Select>
  )
}

/**
 * Given a selected organization, finds the best matching rating profile
 * by industry code, falling back to the baseline profile.
 */
function useRatingProfileMatch(
  selectedOrganization?: BRREGUnit,
  profiles?: Array<RatingProfileSearchResult>,
): number | undefined {
  return useMemo(() => {
    if (!profiles || !profiles.length) {
      return undefined
    }

    // If there's only one profile, always use it
    if (profiles.length === 1) {
      return profiles[0].id
    }

    const industrycode = selectedOrganization?.naeringskode1?.kode
    // No industrycode present, return baseline
    if (!industrycode) {
      return profiles.find(({ isBaseline }) => isBaseline)?.id
    }
    const profileIdsByNames = profiles.reduce(
      (obj, { id, name }) => {
        obj[name] = id
        return obj
      },
      {} as Record<string, number>,
    )
    if (profileIdsByNames[industrycode]) {
      return profileIdsByNames[industrycode]
    }
    const [primaryCode, subcode] = industrycode.split(".")
    // If no profile for 5.1123, also check 5.112, 5.11, 5.1 and 5
    for (let length = subcode.length - 1; length >= 0; length--) {
      const partialSubcode = subcode.substring(0, length)
      const searchCode = partialSubcode ? `${primaryCode}.${partialSubcode}` : primaryCode
      const resultProfile = profileIdsByNames[searchCode]
      if (resultProfile) {
        return resultProfile
      }
    }
    // Otherwise return baseline profile
    return profiles.find(({ isBaseline }) => isBaseline)?.id
  }, [selectedOrganization, profiles])
}
